import {
  Extension,
  combineTransactionSteps,
  getChangedRanges,
  findChildrenInRange,
} from '@tiptap/core';
import { Plugin, PluginKey, Transaction, EditorState } from '@tiptap/pm/state';
import { EditorView } from '@tiptap/pm/view';
import { v4 } from 'uuid';

interface UniqueIdOptions {
  attributeName: string;
  createId: () => string;
}

const existingIds = new Set<string>();

function addIds(state: EditorState, view: EditorView, options: UniqueIdOptions) {
  const { tr, doc } = state;
  const { attributeName, createId } = options;

  let changes = false;

  doc.descendants((node, pos) => {
    if (node.type.name === 'codeBlock') {
      return false;
    }

    if (node.isTextblock && !node.attrs[attributeName]) {
      const nodeId = createId();

      tr.setNodeMarkup(pos, undefined, {
        ...node.attrs,
        [attributeName]: nodeId,
      });

      existingIds.add(nodeId);
      changes = true;
    }
  });

  if (changes) {
    tr.setMeta('addToHistory', false);
    view.dispatch(tr);
  }
}

export default new Extension<UniqueIdOptions>({
  name: 'unique-id',
  priority: 99999,
  addOptions: () => ({
    attributeName: 'id',
    createId: () => v4(),
  }),
  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading'],
        attributes: {
          [this.options.attributeName]: {
            default: null,
            rendered: true,
            keepOnSplit: false,
          },
        },
      },
    ];
  },

  onCreate() {
    addIds(this.editor.state, this.editor.view, this.options);
  },

  addProseMirrorPlugins() {
    const { attributeName, createId } = this.options;

    return [
      new Plugin({
        key: new PluginKey('unique-id'),
        appendTransaction(transactions, oldState, newState) {
          if (!transactions.some(({ docChanged }) => docChanged)) {
            return null;
          }

          let changes = false;
          const tr = newState.tr; // Ensure using the transaction from newState

          const combinedSteps = combineTransactionSteps(
            oldState.doc,
            transactions as Transaction[],
          );

          const changedRanges = getChangedRanges(combinedSteps);

          changedRanges.forEach(({ newRange }) => {
            const nodesInRange = findChildrenInRange(
              newState.doc,
              newRange,
              node => node.isTextblock,
            );

            nodesInRange.forEach(({ node, pos }) => {
              const existingId: string | null | undefined = node.attrs[attributeName];

              if (!existingId) {
                const nodeId = createId();

                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  [attributeName]: nodeId,
                });

                existingIds.add(nodeId);
                changes = true;
              }
            });
          });

          return changes ? tr : null;
        },
      }),
    ];
  },
});
